import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardTerritoryShapeLook = 'industry-territory-shape' | 'industry-territory-shape-on' | 'industry-territory-zone-remove';
export type WedgeOnboardTerritoryZipVariant = 'wedge-onb-zip';

const LOOKS = new Set<string>(['industry-territory-shape', 'industry-territory-shape-on', 'industry-territory-zone-remove']);
export const isWedgeOnboardTerritoryShapeLook = (look: string): look is WedgeOnboardTerritoryShapeLook => LOOKS.has(look);
export const isWedgeOnboardTerritoryZipVariant = (variant: unknown): variant is WedgeOnboardTerritoryZipVariant => variant === 'wedge-onb-zip';

/* Released onboarding territory zip, shape and zones controls (prep-screens-1 OnboardTerritory). The shape chip is a 44px inline-flex pill with a
   5px gap and 9px by 14px of padding, 12.5px semibold type on the elevated fill and the card border; the chosen chip fills with its own hue (the
   caller sets --pill-accent) and wears a two-layer glow of that hue, and its fill, border and glow ease over 160ms and its transform over 140ms.
   The zone remove mark is a bare flex button around a glyph in the accent ink, with no fill, border or padding. The zip input is a full width
   box of 13px by 14px padding on the surface fill and a 1px card border with a 13px radius, 15.5px type on the label ink with 0.5px of letter
   spacing and no outline. Each reads the exact legacy roles, in the order of the source. The caller owns the labels, the glyphs, the handlers,
   the shipped wedge-pill and wedge-onb-field classes, the --pill-accent value, the pop animation and every attribute, including
   autoComplete="postal-code". No look draws the transparent target: the industry- prefix leaves it off, a chip is 44px tall already and the
   remove mark sits inside a 7px padded zone chip, where a target would overlap the chip and its neighbors. */
const CHIP_PADDING = '9px 14px', CHIP_MIN = 44, CHIP_FONT = 12.5, CHIP_GAP = 5, CHIP_RING = 22, CHIP_GLOW = 24, ROUND = 999;
const ZIP_PADDING = '13px 14px', ZIP_RADIUS = 13, ZIP_FONT = 15.5, ZIP_SPACING = 0.5;

export function wedgeOnboardTerritoryShapeStyle(look: WedgeOnboardTerritoryShapeLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'industry-territory-zone-remove') return {background: 'none', border: 0, color: r('accent'), cursor: 'pointer', padding: 0, display: 'flex'};
  const on = look === 'industry-territory-shape-on';
  const hue = 'var(--pill-accent)';
  return {
    padding: CHIP_PADDING, borderRadius: ROUND, minHeight: CHIP_MIN, boxSizing: 'border-box',
    background: on ? hue : r('bgElev'),
    color: on ? r('onAccent') : r('labelSec'),
    border: on ? `1px solid ${hue}` : `1px solid ${r('cardBorder')}`,
    boxShadow: on ? `0 0 0 3px color-mix(in srgb, ${hue} ${CHIP_RING}%, transparent), 0 2px 12px color-mix(in srgb, ${hue} ${CHIP_GLOW}%, transparent)` : 'none',
    transition: `background 160ms ${r('ease')}, border-color 160ms ${r('ease')}, box-shadow 160ms ${r('ease')}, transform 140ms ${r('ease')}`,
    cursor: 'pointer', fontSize: CHIP_FONT, fontWeight: 600, fontFamily: r('font'),
    display: 'inline-flex', alignItems: 'center', gap: CHIP_GAP,
  };
}

export function wedgeOnboardTerritoryZipStyle(_variant: WedgeOnboardTerritoryZipVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', padding: ZIP_PADDING, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: ZIP_RADIUS,
    fontSize: ZIP_FONT, color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box', letterSpacing: ZIP_SPACING,
  };
}
